import {
  Label,
  PrimaryButton,
  DefaultButton,
  TextField,
  ProgressIndicator,
  MessageBar,
  MessageBarType,
  IconButton,
  IButton,
  DialogType,
  getId,
  Spinner,
  SpinnerSize,
  Dropdown,
  IDropdownOption,
  Stack,
  Separator,
  ShimmeredDetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  Selection,
  Dialog,
  DialogFooter,
  Icon,
  Checkbox
} from '@fluentui/react';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { msalAuth } from '../../shared/auth/MsalAuthProvider';
import { IUser } from '../../shared/models/User.model';
import { UIConstants } from '../../shared/models/Constants';
import ITenant from '../../shared/models/Tenant.model';
import { Utility } from '../../shared/models/Helper';
import AccessAPI from '../../store/AccessAPI';
import { Role, RoleManagementRequest, AttributeType } from "../../shared/models/Role.model";

// Attributes that should be auto-handled by backend and not displayed
const SYSTEM_ATTRIBUTES = ['UserAlias', 'RoleSetToExpire'];

interface AttributeConfig {
  name: string;
  required: boolean;
  // Store the UI element type for this attribute (Dropdown / Multi-select)
  attributeType?: string;
}
interface DisplayData {
  name: string,
  description: string,
  policy: string,
  attributes: string,
  createdBy: string,
  updatedBy: string,
  businessGroup: string
}

const RoleManagement: React.FC = () => {
  const { tenantName } = useParams<{ tenantName: string }>();
  const history = useHistory();

  // State
  const [userDetails, setUserDetails] = useState<IUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [displayRoles, setDisplayRoles] = useState<DisplayData[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<MessageBarType>(MessageBarType.info);
  const [hideDialog, setHideDialog] = useState<boolean>(true);
  const [hideDeleteDialog, setHideDeleteDialog] = useState<boolean>(true);
  const [isCreatingRole, setIsCreatingRole] = useState<boolean>(false);
  const [isTenantAdmin, setIsTenantAdmin] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  // Form state for role creation/editing
  const [roleName, setRoleName] = useState<string>('');
  const [businessGroup, setBusinessGroup] = useState<string>('');
  const [roleDescription, setRoleDescription] = useState<string>('');
  const [selectedPolicy, setSelectedPolicy] = useState<string>('');
  const [attributeConfigs, setAttributeConfigs] = useState<AttributeConfig[]>([]);
  const [availablePolicies, setAvailablePolicies] = useState<IDropdownOption[]>([]);
  const [availableAttributes, setAvailableAttributes] = useState<IDropdownOption[]>([]);
  const [selectedAttributeToAdd, setSelectedAttributeToAdd] = useState<string>('');
  const [policiesMap, setPoliciesMap] = useState<{ [key: string]: string }>({});
  const [searchText, setSearchText] = useState<string>('');
  const [useSkipLevelManager, setUseSkipLevelManager] = useState<boolean>(false);

  const attributeElementOptions: IChoiceGroupOption[] = [
    { key: AttributeType.List, text: 'Dropdown' },
    { key: AttributeType.MultiSelectList, text: 'Multi-select' }
  ];

  // Refs
  const tenantDataRef = useRef<ITenant | null>(null);
  const labelId = useRef<string>(getId('dialogLabel'));
  const subTextId = useRef<string>(getId('subTextLabel'));
  const alertMsgCloseBtnRef = useRef<IButton>(null);
  const rolesRef = useRef<Role[]>([]);

  // Initialize Selection once with stable callback
  const handleSelectionChange = useCallback(() => {
    const selectedItems = selectionRef.current.getSelection();
    if (selectedItems.length > 0) {
      const selectedItem = selectedItems[0] as any;
      if (tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) {
        const selectedRole = rolesRef.current.find(r => r.name === selectedItem.name && r.group === selectedItem.businessGroup);
        setSelectedRole(selectedRole || null);
      }
      else {
      const role = rolesRef.current.find(r => r.name === selectedItem.name);
      setSelectedRole(role || null);
      }
    } else {
      setSelectedRole(null);
    }
  }, []);

  const selectionRef = useRef<Selection>(
    new Selection({
      onSelectionChanged: () => {
        handleSelectionChange();
      }
    })
  );

  // Update rolesRef when roles change
  useEffect(() => {
    rolesRef.current = roles;
  }, [roles]);

  /**
   * Show message to user
   */
  const showMessageToUser = useCallback((msg: string, msgType: MessageBarType = MessageBarType.info): void => {
    setShowMessage(true);
    setMessage(msg);
    setMessageType(msgType);

    setTimeout(() => {
      setShowMessage(false);
    }, 3000);

    setTimeout(() => {
      alertMsgCloseBtnRef.current?.focus();
    }, 100);
  }, []);

  /**
   * Filter out system attributes from display
   */
  const filterDisplayAttributes = useCallback((attributes: string[]): string[] => {
    if (!attributes) return [];
    return attributes.filter(attr => !SYSTEM_ATTRIBUTES.includes(attr) && attr != "BusinessGroup");
  }, []);


  /**
 * Load all roles for the tenant
 */
  const loadRoles = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      let response: any;
      if (tenantDataRef.current!.isExternalAADTenant) {
        response = await AccessAPI.getCosmosRoleDefinitions(tenantDataRef.current!.TenantId);

        // Convert external AAD roles to Role data format
        if (response && Array.isArray(response)) {
          const rolesData: Role[] = response.map((externalRole: any) => ({
            id: `${externalRole.roleId}:${externalRole.businessGroupName}`,
            name: externalRole.roleName || '',
            description: externalRole.description || '',
            permissions: [{
              actions: [externalRole.policyType],
              notActions: [],
              condition: ''
            }],
            scopes: [],
            sortOrder: '0',
            attributes: [],
            approvalPolicy: externalRole.policyType || '',
            useSkipLevelManager: typeof externalRole.allowSkipLevel === 'string'
              && externalRole.allowSkipLevel.toLowerCase() === 'allowalternatemanager:true',
            createdBy: externalRole.createdBy,
            createdDate: externalRole.createdOn,
            updatedBy: externalRole.updatedBy,
            updatedDate: externalRole.updatedOn,
            group: externalRole.businessGroupName
          }));

          response = {
            IsSuccess: true,
            IsDataLoaded: true,
            Data: rolesData
          };
        } else {
          response = {
            IsSuccess: false,
            IsDataLoaded: true,
            Data: []
          };
        }
      }
      else if (tenantDataRef.current!.EnableRoleGrouping) {
        response = await AccessAPI.requestRoleByTenant(tenantDataRef.current!.TenantId, true);

        // Fetch business group to role mapping
        const businessGroupResponse = await AccessAPI.getBusinessGroupByTenant(tenantDataRef.current!.TenantId);

        if (response && response.IsSuccess && response.Data && businessGroupResponse?.IsSuccess && businessGroupResponse?.Data) {
          const rolesData: Role[] = response.Data;
          const businessGroupData = businessGroupResponse.Data;
          const expandedRoles: Role[] = [];

          // Create a map of role name to business groups
          const roleToBusinessGroupMap: { [roleName: string]: string[] } = {};

          if (businessGroupData.businessGroups && Array.isArray(businessGroupData.businessGroups)) {
            businessGroupData.businessGroups.forEach((bg: any) => {
              const businessGroupName = bg.businessGroupName;
              if (bg.roles && Array.isArray(bg.roles)) {
                bg.roles.forEach((role: any) => {
                  const roleName = role.roleName;
                  if (!roleToBusinessGroupMap[roleName]) {
                    roleToBusinessGroupMap[roleName] = [];
                  }
                  if (!roleToBusinessGroupMap[roleName].includes(businessGroupName)) {
                    roleToBusinessGroupMap[roleName].push(businessGroupName);
                  }
                });
              }
            });
          }

          // Expand roles based on business group mappings
          rolesData.forEach((role) => {
            const businessGroups = roleToBusinessGroupMap[role.name] || [];
            const otherAttributes = (role.attributes || []).filter(attr => !SYSTEM_ATTRIBUTES.includes(attr));

            if (businessGroups.length === 0) {
              // No business group mapping - keep role as is
              expandedRoles.push(role);
            } else {
              // Create a separate role entry for each business group
              businessGroups.forEach((businessGroup) => {
                // Combine business group with any other attributes
                const combinedAttributes = [...otherAttributes];

                const expandedRole: Role = {
                  ...role,
                  // Create unique identifier combining role name and business group
                  id: `${role.id}:${businessGroup}`,
                  name: role.name,
                  // Store business group as primary attribute, keep others if present
                  attributes: combinedAttributes,
                  description: role.description,
                  permissions: role.permissions,
                  scopes: role.scopes,
                  sortOrder: role.sortOrder,
                  approvalPolicy: role.approvalPolicy,
                  createdBy: role.createdBy,
                  createdDate: role.createdDate,
                  updatedBy: role.updatedBy,
                  updatedDate: role.updatedDate,
                  group: businessGroup
                };
                expandedRoles.push(expandedRole);
              });
            }
          });

          // Update response with expanded roles
          response = {
            IsSuccess: true,
            IsDataLoaded: true,
            Data: expandedRoles
          };
        }
      }
      else {
        response = await AccessAPI.requestRoleByTenant(tenantDataRef.current!.TenantId, true);
      }

      if (response && response.IsSuccess && response.Data) {
        let rolesData: Role[] = response.Data;
        response.Data.map((role: Role) => {
          role.approvalPolicy = role.permissions[0]?.actions[0]?.split("Policy:")[1] || '';
          // Parse skip-level manager flag from the role's actions (stored as AllowAlternateManager:true|false)
          if (!tenantDataRef.current!.isExternalAADTenant) {
            const skipLevelAction = role.permissions[0]?.actions?.find(a => a && a.toLowerCase().startsWith('allowalternatemanager:'));
            role.useSkipLevelManager = !!skipLevelAction && skipLevelAction.split(':')[1]?.toLowerCase() === 'true';
          }
        });
        if (tenantDataRef.current!.TenantName == "FDL") {
          rolesData = rolesData.filter((role: Role) => {
            const hasUIDisplayFlagFalse = role.permissions.some(permission =>
              permission.actions.some(action => action.includes("UIDisplayFlag:false"))
            );
            return !hasUIDisplayFlagFalse;
          });
        }
        setRoles(rolesData);

        // Prepare display data - show actual attribute names instead of count
        const displayData = rolesData.map(role => {
          const displayAttrs = filterDisplayAttributes(role.attributes || []);
          return {
            name: role.name,
            description: role.description,
            policy: role.permissions[0]?.actions[0]?.split("Policy:")[1] || 'N/A',
            attributes: displayAttrs.length > 0 ? displayAttrs.join(', ') : 'None',
            createdBy: role.createdBy ?? 'N/A',
            updatedBy: role.updatedBy ?? 'N/A',
            businessGroup: role.group
          };
        });
        setDisplayRoles(displayData);
      } else {
        setRoles([]);
        setDisplayRoles([]);
        setSelectedRole(null);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      showMessageToUser('Failed to load roles. Please try again.', MessageBarType.error);
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  }, [showMessageToUser, filterDisplayAttributes]);

  /**
   * Load available approval policies
   */
  const loadAvailablePolicies = useCallback(async (): Promise<void> => {
    try {
      const result = await AccessAPI.getAvailablePolicies(tenantDataRef.current!.TenantId);

      if (result && Array.isArray(result)) {
        // Create a map of policy IDs to names for display
        const policyMap: { [key: string]: string } = {};
        const policyOptions: IDropdownOption[] = result.map(policy => {
          policyMap[policy.id] = policy.name;
          return {
            key: policy.id,
            text: policy.name
          };
        });

        setPoliciesMap(policyMap);
        setAvailablePolicies(policyOptions);
      } else {
        setAvailablePolicies([]);
        setPoliciesMap({});
      }
    } catch (err) {
      console.error('Error loading policies:', err);
      showMessageToUser('Failed to load policies. Please try again.', MessageBarType.error);
      setAvailablePolicies([]);
      setPoliciesMap({});
    }
  }, [showMessageToUser]);

  /**
 * Load available attributes
 */
  const loadAvailableAttributes = useCallback(async (): Promise<void> => {
    try {
      if (!tenantDataRef.current?.isExternalAADTenant) {
        const response = await AccessAPI.getAttributesByTenantId(tenantDataRef.current!.TenantId);
        if (response && response.Data && response.Data.attributes) {
          const attributeOptions: IDropdownOption[] = response.Data.attributes
            .filter((attr: any) => !SYSTEM_ATTRIBUTES.includes(attr.name))
            .map((attr: any) => ({
              key: attr.name,
              text: attr.name
            }));
          setAvailableAttributes(attributeOptions);
        } else {
          setAvailableAttributes([]);
        }
      }
      else {
        setAvailableAttributes([]);
      }
    } catch (error) {
      console.error('Error loading attributes:', error);
      showMessageToUser('Failed to load attributes. Ensure attributes exist for this tenant.', MessageBarType.warning);
      setAvailableAttributes([]);
    }
  }, [showMessageToUser]);

  useEffect(() => {
    const initializeComponent = async () => {
      const userAccount = msalAuth.getAccount();
      const user: IUser = {
        PrincipalId: userAccount.accountIdentifier,
        Alias: userAccount.userName,
        Name: userAccount.name
      };

      setUserDetails(user);

      var TenantData = Utility.GetTenantData();
      const tenant = TenantData.filter(x => x.TenantName.toUpperCase() === tenantName.toUpperCase())[0];

      if (tenant) {
        tenantDataRef.current = tenant;

        // Check if user is admin
        const managementAccess = await AccessAPI.GetManagementAccess(tenant.TenantId);
        setIsTenantAdmin(managementAccess.roleManagement);

        if (managementAccess.roleManagement) {
          await Promise.all([
            loadAvailablePolicies(),
            loadAvailableAttributes()
          ]);
          // Load roles after policies are loaded to properly display policy names
          await loadRoles();
        } else {
          showMessageToUser('You do not have permission to manage roles.', MessageBarType.error);
          setIsInitializing(false);
        }
      }
    };

    initializeComponent();
  }, [tenantName]);

  /**
 * Validate role form
 */
  const validateRoleForm = useCallback((): boolean => {
    const alphanumericUnderscoreRegex = /^[a-zA-Z0-9_ ]+$/;

    if (!roleName.trim()) {
      showMessageToUser('Role name is required.', MessageBarType.error);
      return false;
    }
    if (!alphanumericUnderscoreRegex.test(roleName) && isCreatingRole) {
      showMessageToUser('Role name can only contain alphabets, numbers, spaces, and underscores.', MessageBarType.error);
      return false;
    }
    if (!selectedPolicy) {
      showMessageToUser('Please select an approval policy.', MessageBarType.error);
      return false;
    }
    if ((tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) && !businessGroup.trim()) {
      showMessageToUser('Business group is required.', MessageBarType.error);
      return false;
    }
    if (businessGroup != '') {
      if (!alphanumericUnderscoreRegex.test(businessGroup) && isCreatingRole) {
        showMessageToUser('Business group name can only contain alphabets, numbers, and underscores.', MessageBarType.error);
        return false;
      }
    }
    // Validate each attribute name
    for (const attr of attributeConfigs) {
      if (!alphanumericUnderscoreRegex.test(attr.name) && isCreatingRole) {
        showMessageToUser(`Attribute "${attr.name}" can only contain alphabets, numbers, and underscores.`, MessageBarType.error);
        return false;
      }
    }
    return true;
  }, [roleName, businessGroup, selectedPolicy, attributeConfigs, showMessageToUser]);

  /**
   * Create or update role
   */
  const saveRole = useCallback(async (): Promise<void> => {
    if (!validateRoleForm()) return;

    setIsSaving(true);

    try {
      // work on a local copy to avoid mutating state directly
      let updatedAttributeConfigs = [...attributeConfigs];
      if ((tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) && businessGroup != '' && !updatedAttributeConfigs.some(ac => ac.name === "BusinessGroup")) {
        updatedAttributeConfigs.push({ name: "BusinessGroup", required: true, attributeType: AttributeType.List });
      }
      const request: RoleManagementRequest = {
        operation: isCreatingRole ? 'create' : 'update',
        roleDetails: {
          id: isCreatingRole ? undefined : selectedRole?.id,
          name: roleName.trim(),
          description: roleDescription,
          approvalPolicy: selectedPolicy,
          attributes: updatedAttributeConfigs.map(ac => ac.name),
          // include element types chosen by admin for each attribute (exclude BusinessGroup so it won't be part of AttributesDisplay)
          AttributeElementTypes: updatedAttributeConfigs
            .filter(ac => ac.name !== 'BusinessGroup')
            .reduce((acc, ac) => {
              acc[ac.name] = ac.attributeType || AttributeType.List;
              return acc;
            }, {} as { [key: string]: string }),
          permissions: [],
          scopes: [],
          sortOrder: '0',
          group: businessGroup != null && businessGroup != '' ? businessGroup.trim() : '',
          useSkipLevelManager: useSkipLevelManager
        }
      };

      const response = await AccessAPI.manageRole(tenantDataRef.current!.TenantId, request);

      if (response && response.data && response.data.success) {
        showMessageToUser(
          isCreatingRole ? 'Role created successfully.' : 'Role updated successfully.',
          MessageBarType.success
        );
        setHideDialog(true);
        await loadRoles();
        setSelectedRole(null);
      } else {
        showMessageToUser(
          response?.data?.message || 'Failed to save role. Please try again.',
          MessageBarType.error
        );
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showMessageToUser('An error occurred while saving the role.', MessageBarType.error);
    } finally {
      setIsSaving(false);
    }
  }, [validateRoleForm, isCreatingRole, selectedRole, roleName, businessGroup, roleDescription, selectedPolicy, attributeConfigs, useSkipLevelManager, showMessageToUser, loadRoles]);

  /**
   * Confirm delete role
   */
  const confirmDeleteRole = useCallback(async (): Promise<void> => {
    if (!selectedRole) return;

    setIsSaving(true);

    try {
      const request: RoleManagementRequest = {
        operation: 'delete',
        roleDetails: {
          id: selectedRole.id,
          name: selectedRole.name,
          description: '',
          approvalPolicy: '',
          attributes: [],
          permissions: [],
          scopes: [],
          sortOrder: '0'
        }
      };

      const response = await AccessAPI.manageRole(tenantDataRef.current!.TenantId, request);

      if (response && response.data && response.data.success) {
        showMessageToUser('Role deleted successfully.', MessageBarType.success);
        setHideDeleteDialog(true);
        setSelectedRole(null);
        await loadRoles();
      } else {
        showMessageToUser(
          response?.data?.message || 'Failed to delete role. Please try again.',
          MessageBarType.error
        );
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showMessageToUser('An error occurred while deleting the role.', MessageBarType.error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedRole, showMessageToUser, loadRoles]);

  /**
   * Handle create new role click
   */
  const onCreateNewRoleClick = useCallback((): void => {
    setIsCreatingRole(true);
    setRoleName('');
    setBusinessGroup('');
    setRoleDescription('');
    setSelectedPolicy('');
    setAttributeConfigs([]);
    setSelectedAttributeToAdd('');
    setUseSkipLevelManager(false);
    setHideDialog(false);
    setSelectedRole(null);
    selectionRef.current.setAllSelected(false);
  }, []);

  /**
   * Handle edit role click
   */
  const onEditRoleClick = useCallback((): void => {
    if (!selectedRole) return;

    setIsCreatingRole(false);
    setRoleName(selectedRole.name);
    setBusinessGroup(selectedRole.group);
    setRoleDescription(selectedRole.description || '');
    setSelectedPolicy(selectedRole.approvalPolicy || '');

    const displayAttrs = filterDisplayAttributes(selectedRole.attributes || []);
    setAttributeConfigs(displayAttrs.map(attr => ({ name: attr, required: false })));
    setSelectedAttributeToAdd('');
    setUseSkipLevelManager(!!selectedRole.useSkipLevelManager);
    setHideDialog(false);
  }, [selectedRole, filterDisplayAttributes]);

  /**
   * Handle delete role click
   */
  const onDeleteRoleClick = useCallback((): void => {
    if (!selectedRole) return;
    setHideDeleteDialog(false);
  }, [selectedRole]);

  /**
  * Validate search
  */
  const validateSearch = useCallback((): void => {
    if (searchText != '') {
      const filtered = rolesRef.current.filter(x =>
        x.name.toLowerCase().includes(searchText.toLowerCase())
      );
      const displayData = filtered.map(role => {
        const displayAttrs = filterDisplayAttributes(role.attributes || []);
        return {
          name: role.name,
          description: role.description,
          policy: role.permissions[0]?.actions[0]?.split("Policy:")[1] || 'N/A',
          attributes: displayAttrs.length > 0 ? displayAttrs.join(', ') : 'None',
          createdBy: role.createdBy ?? 'N/A',
          updatedBy: role.updatedBy ?? 'N/A',
          businessGroup: role.group
        };
      });
      setDisplayRoles(displayData);
    } else {
      const displayData = rolesRef.current.map(role => {
        const displayAttrs = filterDisplayAttributes(role.attributes || []);
        return {
          name: role.name,
          description: role.description,
          policy: role.permissions[0]?.actions[0]?.split("Policy:")[1] || 'N/A',
          attributes: displayAttrs.length > 0 ? displayAttrs.join(', ') : 'None',
          createdBy: role.createdBy ?? 'N/A',
          updatedBy: role.updatedBy ?? 'N/A',
          businessGroup: role.group
        };
      });
      setDisplayRoles(displayData);
    }
  }, [displayRoles, searchText]);
  /**
   * Add attribute to role
   */
  const addAttribute = (attributeName: string): void => {
    if (!attributeName) return;

    const exists = attributeConfigs.some(ac => ac.name === attributeName);
    if (!exists) {
      setAttributeConfigs([...attributeConfigs, { name: attributeName, required: false, attributeType: AttributeType.List }]);
    }
    setSelectedAttributeToAdd('');
  };

  /**
   * Remove attribute from role
   */
  const removeAttribute = (attributeName: string): void => {
    setAttributeConfigs(attributeConfigs.filter(ac => ac.name !== attributeName));
  };

  /**
   * Navigate to Attribute Management
   */
  const navigateToAttributeManagement = (): void => {
    history.push(`/request/${UIConstants.RouteConfig.Administration}/${UIConstants.RouteConfig.AttributeManagement}/${tenantName}`);
  };

  /**
   * Navigate to Approval Policy
   */
  const navigateToApprovalPolicy = (): void => {
    history.push(`/request/${UIConstants.RouteConfig.Administration}/${UIConstants.RouteConfig.ApprovalPolicy}/${tenantName}`);
  };

  /**
   * Render message bar
   */
  const renderMessage = (): JSX.Element => {
    return (
      <MessageBar
        messageBarType={messageType}
        isMultiline={false}
        onDismiss={() => setShowMessage(false)}
        dismissButtonAriaLabel={UIConstants.ButtonText.Close}
        aria-live="assertive"
        role="alert"
      >
        {message}
      </MessageBar>
    );
  };

  /**
   * Column headers for roles list
   */
  const roleColumns = (): IColumn[] => {
    return [
      { key: 'name', name: 'Role Name', fieldName: 'name', minWidth: 150, maxWidth: 250, isResizable: true },
      (tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) && { key: 'businessGroup', name: 'Business Group', fieldName: 'businessGroup', minWidth: 200, maxWidth: 300, isResizable: true },
      (!tenantDataRef.current?.isExternalAADTenant) && { key: 'description', name: 'Description', fieldName: 'description', minWidth: 200, maxWidth: 300, isResizable: true },
      { key: 'policy', name: 'Policy', fieldName: 'policy', minWidth: 150, maxWidth: 200, isResizable: true },
      (!tenantDataRef.current?.isExternalAADTenant) && { key: 'attributes', name: 'Attributes', fieldName: 'attributes', minWidth: 200, maxWidth: 300, isResizable: true },
      { key: 'createdby', name: 'Created By', fieldName: 'createdBy', minWidth: 200, maxWidth: 300, isResizable: true },
      { key: 'updatedby', name: 'Updated By', fieldName: 'updatedBy', minWidth: 200, maxWidth: 300, isResizable: true }
    ].filter(Boolean) as IColumn[];
  };

  return (
    <React.Fragment>
      <div id={'div-msg-area'} style={{ height: 25 }}>
        {showMessage && renderMessage()}
        {isSaving && <ProgressIndicator label="Processing..." description="Please wait while we process your request." />}
      </div>
      <div id={'div-role-section'} style={{ padding: '1%', paddingTop: '2%' }}>
        <div key="grid-role" className={"ms-Grid"} dir="ltr">
          <div key='grid-row-title' className={"ms-Grid-row"}>
            <div className={"ms-Grid-col ms-sm6 ms-md6 ms-lg9"}>
              <Label style={{ paddingLeft: 0, fontSize: 20 }}>
                <h1 role="heading" aria-label={UIConstants.PageTitle.RoleManagement} aria-level={1} className={"h1"} style={{ paddingLeft: 0, fontSize: 20, margin: 0 }}>
                  {UIConstants.PageTitle.RoleManagement}
                </h1>
              </Label>
            </div>
          </div>

          {!isInitializing && isTenantAdmin && (
            <div key='grid-row-actions' className={"ms-Grid-row"} style={{ paddingTop: 10, paddingBottom: 10 }}>
              <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text="Create New Role" onClick={onCreateNewRoleClick} iconProps={{ iconName: 'AddIn' }} />
                <DefaultButton text="Edit Role" onClick={onEditRoleClick} disabled={!selectedRole} iconProps={{ iconName: 'Edit' }} />
                <DefaultButton text="Delete Role" onClick={onDeleteRoleClick} disabled={!selectedRole} iconProps={{ iconName: 'Delete' }} />
                <TextField placeholder="Search for role name" value={searchText} onChange={(ev, text) => setSearchText(text || '')} />
                <PrimaryButton title="Search" onClick={validateSearch}> <Icon iconName={'Search'} />  </PrimaryButton>
                <DefaultButton title="Refresh" onClick={() => loadRoles()}> <Icon iconName={'Refresh'} />&nbsp;Refresh </DefaultButton>
              </Stack>
            </div>
          )}

          <div key='grid-row-list' className={"ms-Grid-row"} style={{ paddingTop: 10 }}>
            {isLoading ? (
              <Spinner size={SpinnerSize.large} label="Loading roles..." ariaLive="assertive" labelPosition="bottom" />
            ) : displayRoles.length > 0 ? (
              <ShimmeredDetailsList
                setKey="role-items"
                columns={roleColumns()}
                items={displayRoles}
                selection={selectionRef.current}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.single}
                enableShimmer={false}
                ariaLabelForShimmer="Roles are being fetched"
                ariaLabelForGrid="Role definitions"
                listProps={{ renderedWindowsAhead: 10, renderedWindowsBehind: 10 }}
              />
            ) : (
              <Label>No roles configured for this tenant.</Label>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Role Dialog */}
      <Dialog
        hidden={hideDialog}
        onDismiss={() => setHideDialog(true)}
        dialogContentProps={{
          type: DialogType.normal,
          title: isCreatingRole ? 'Create New Role' : 'Edit Role',
          closeButtonAriaLabel: UIConstants.ButtonText.Close,
          subText: isCreatingRole ? 'Create a new role for this tenant' : 'Edit the selected role'
        }}
        modalProps={{
          titleAriaId: labelId.current,
          subtitleAriaId: subTextId.current,
          isBlocking: false,
          styles: {
            main: {
              maxWidth: '800px !important',
              minWidth: '750px !important'
            }},
        }}
      >
        <Stack tokens={{ childrenGap: 15 }}>
          <TextField
            label="Role Name"
            value={roleName}
            onChange={(e, newValue) => setRoleName(newValue || '')}
            required
            disabled={!isCreatingRole}
          />
          {(tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) && <TextField
            label="BusinessGroup"
            value={businessGroup}
            onChange={(e, newValue) => setBusinessGroup(newValue || '')}
            required
            disabled={!isCreatingRole}
          />}
          {!tenantDataRef.current?.isExternalAADTenant && <TextField
            label="Description"
            value={roleDescription}
            onChange={(e, newValue) => setRoleDescription(newValue || '')}
            multiline
            rows={3}
          />}
          <Stack>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
              <Dropdown
                label="Approval Policy"
                selectedKey={selectedPolicy}
                onChange={(e, option) => setSelectedPolicy(option?.key as string || '')}
                options={availablePolicies}
                required
                placeholder="Select a policy"
                styles={{ root: { flexGrow: 1 } }}
              />
              <DefaultButton
                text="Create"
                onClick={navigateToApprovalPolicy}
                iconProps={{ iconName: 'Add' }}
                title="Create a new approval policy"
                styles={{ root: { height: '38px !important' } }}
              />
            </Stack>
            {selectedPolicy && (
              <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 5 } }}>
                Approval policy will be applied to the role upon submission. Current selection: <strong>{policiesMap[selectedPolicy] || selectedPolicy}</strong>.
                {(tenantDataRef.current?.isExternalAADTenant || tenantDataRef.current?.EnableRoleGrouping) && <span> Any changes made to the approval policy of an existing role are automatically applied to all Business Groups associated with that role, including those created earlier.</span>}
              </MessageBar>
            )}
            {availablePolicies.length === 0 && (
              <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: 5 } }}>
                No policies available. Click "Create" to create one.
              </MessageBar>
            )}
          </Stack>
          <Stack tokens={{ childrenGap: 4 }}>
            <Checkbox
              label="Use Skip Level Manager"
              checked={useSkipLevelManager}
              onChange={(ev, checked) => setUseSkipLevelManager(!!checked)}
            />
            <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: 5 } }}>
              This should be selected only when a manager level is present for approval.
            </MessageBar>
          </Stack>
          {!tenantDataRef.current?.isExternalAADTenant &&
            <Stack>
              {isCreatingRole && <Separator>Attributes</Separator>}
              {isCreatingRole && <Stack>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                  <Dropdown
                    placeholder="Select attribute to add"
                    selectedKey={selectedAttributeToAdd}
                    options={availableAttributes.filter(ao => !attributeConfigs.some(ac => ac.name === ao.key))}
                    onChange={(e, option) => {
                      if (option) {
                        setSelectedAttributeToAdd(option.key as string);
                        addAttribute(option.key as string);
                      }
                    }}
                    disabled={!isCreatingRole}
                    styles={{ root: { flexGrow: 1 } }}
                  />
                  <DefaultButton
                    text="Create"
                    onClick={navigateToAttributeManagement}
                    iconProps={{ iconName: 'Add' }}
                    title="Create a new attribute"
                    styles={{ root: { height: '38px !important' } }}
                  />
                </Stack>
                {availableAttributes.length === 0 && (
                  <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: 5 } }}>
                    No attributes available. Click "Create" to create one.
                  </MessageBar>
                )}
              </Stack>}

                {attributeConfigs.length > 0 && (
                  <Stack tokens={{ childrenGap: 5 }}>
                    <Label>Selected Attributes:</Label>
                    {attributeConfigs.map((ac, index) => (
                    ac.name != "BusinessGroup" ? (
                      <Stack key={index} horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { padding: '5px 10px', backgroundColor: '#f3f2f1', borderRadius: 4 } }}>
                        <Label styles={{ root: { flexGrow: 1, marginBottom: 0 } }}>{ac.name}</Label>
                        {/* Choice for element type - only visible when creating a new role */}
                        {isCreatingRole && (
                          <div style={{ marginRight: 10 }}>
                            <ChoiceGroup
                              selectedKey={ac.attributeType || AttributeType.List}
                              options={attributeElementOptions}
                              onChange={(ev, option) => {
                                const updated = attributeConfigs.map(item => item.name === ac.name ? { ...item, attributeType: option?.key as string } : item);
                                setAttributeConfigs(updated);
                              }}
                            />
                          </div>
                        )}
                        <IconButton
                          iconProps={{ iconName: 'Delete' }}
                          title="Remove attribute"
                          ariaLabel="Remove attribute"
                          onClick={() => removeAttribute(ac.name)}
                          disabled={!isCreatingRole}
                        />
                      </Stack>
                    ) : <></>))}
                  </Stack>
                )}
              </Stack>
          }
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={saveRole} text={isCreatingRole ? 'Create' : 'Save'} disabled={isSaving} />
          <DefaultButton onClick={() => setHideDialog(true)} text={UIConstants.ButtonText.Cancel} disabled={isSaving} />
        </DialogFooter>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={hideDeleteDialog}
        onDismiss={() => setHideDeleteDialog(true)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Role',
          closeButtonAriaLabel: UIConstants.ButtonText.Close,
          subText: `Are you sure you want to delete the role "${selectedRole?.name}"? This action cannot be undone.`
        }}
        modalProps={{
          titleAriaId: labelId.current,
          subtitleAriaId: subTextId.current,
          isBlocking: false,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDeleteRole} text="Delete" disabled={isSaving} />
          <DefaultButton onClick={() => setHideDeleteDialog(true)} text={UIConstants.ButtonText.Cancel} disabled={isSaving} />
        </DialogFooter>
      </Dialog>
    </React.Fragment>
  );
};

export default RoleManagement;
